/**
 * AI difficulty and personality settings (spec §20.6). All weights are tunable; the defaults were
 * calibrated with AI-vs-AI self-play (scripts/simulate.ts).
 */

export type Difficulty = 'easy' | 'standard' | 'hard';

export interface Personality {
  /** Display name for campaign regulars; empty for duel opponents. */
  readonly name: string;
  /** Multiplier on the value of capturing enemy pawns. */
  readonly aggression: number;
  /** Multiplier on the expected loss from enemy captures next turn. */
  readonly caution: number;
  /** Multiplier on the value of cards spent (higher = saves cards for later rounds). */
  readonly thrift: number;
  /** Fraction of the opponent's estimated remaining potential that counts as a safe lead to pass on. */
  readonly passLead: number;
  /** Multiplier on the value of Epitaph routes (gifted, kept, or played). */
  readonly epitaphRegard: number;
  /** True to take the first turn when choosing the starter. */
  readonly preferStart: boolean;
  /** Share of its remaining cards the side invests in an open (non-deciding) round. */
  readonly roundShare: number;
}

export interface SearchSettings {
  /** First-link candidates kept for expansion. */
  readonly beam1: number;
  /** Two-link candidates kept for expansion. */
  readonly beam2: number;
  /** Maximum simulated nodes per search. */
  readonly nodeBudget: number;
  /** Plans rescored with the deeper evaluation. */
  readonly refine: number;
  /** Determinizations used when a decision depends on hidden cards. */
  readonly samples: number;
  /** Softmax temperature over plan values (0 = always the best plan). */
  readonly temperature: number;
  /** Whether threats and Epitaph values are evaluated at all. */
  readonly tactical: boolean;
  /** Simulated links for an exhaustive search of the highest-scoring chain (0 = none). */
  readonly chainSearch: number;
  /** How often a decision is a blunder, as people make them: any plan at all, not a good one. */
  readonly blunder?: number;
  /** The longest chain it thinks of (people often stop at one or two links). */
  readonly maxChain?: number;
}

export const DIFFICULTY_SETTINGS: Readonly<Record<Difficulty, SearchSettings>> = Object.freeze({
  // Easy plays like someone new to the game: it often settles for a fair chain over the best,
  // blunders now and then, and never thinks past two links. Normal misjudges less often. Sampled
  // with scripts/sample-difficulty.ts against stand-ins for people (1000 duels each).
  easy: {
    beam1: 10,
    beam2: 6,
    nodeBudget: 600,
    refine: 0,
    samples: 1,
    temperature: 40,
    tactical: false,
    chainSearch: 0,
    blunder: 0.25,
    maxChain: 2,
  },
  standard: {
    beam1: 28,
    beam2: 16,
    nodeBudget: 2500,
    refine: 4,
    samples: 1,
    temperature: 16,
    tactical: true,
    chainSearch: 0,
    blunder: 0.15,
  },
  hard: {
    beam1: 48,
    beam2: 28,
    nodeBudget: 6000,
    refine: 8,
    samples: 3,
    temperature: 0,
    tactical: true,
    chainSearch: 8000,
  },
});

/** Fast greedy search used inside simulations (chases, opponent replies). */
export const GREEDY_SETTINGS: SearchSettings = Object.freeze({
  beam1: 10,
  beam2: 6,
  nodeBudget: 450,
  refine: 0,
  samples: 1,
  temperature: 0,
  tactical: false,
  chainSearch: 0,
});

export const BALANCED: Personality = Object.freeze({
  name: '',
  aggression: 1,
  caution: 1,
  thrift: 1,
  passLead: 0.55,
  epitaphRegard: 1,
  preferStart: false,
  roundShare: 0.4,
});

/** The Broker's regulars (campaign tables 1–3). */
export const REGULARS: readonly Personality[] = Object.freeze([
  {
    name: 'Old Tallow',
    aggression: 0.8,
    caution: 1.3,
    thrift: 1.2,
    passLead: 0.5,
    epitaphRegard: 0.8,
    preferStart: false,
    roundShare: 0.35,
  },
  {
    name: 'The Magpie',
    aggression: 1.35,
    caution: 0.8,
    thrift: 0.9,
    passLead: 0.65,
    epitaphRegard: 1.2,
    preferStart: true,
    roundShare: 0.45,
  },
  {
    name: 'The Widow',
    aggression: 1.1,
    caution: 1.15,
    thrift: 1.05,
    passLead: 0.6,
    epitaphRegard: 1.3,
    preferStart: false,
    roundShare: 0.4,
  },
]);
